import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Row } from 'reactstrap';
import { useAPIErrorContext } from '../../../contexts/apiErrorContext'
import { useFetchWrapper } from "../../../hooks/useFetchWrapper";
import Helper from "../../../utilities/helper";
import { clientConfig } from "../../../utilities/configService";


interface IFleetProps {
    fleetID: string,
    onVehicleCountLoaded?: (vehicleCount: number) => void
}

interface IFleetSummary {
    fleetName: string,
    fleetID: string,
    organizationTypeName: string,
    totalVehicleCount: number,
    fuelTypes: any,
    vehicleTypes: any,
    vehicleSets: IVehicleSet[],
    fleetCreatedDate: Date,
    fleetUpdatedDate: Date,
    fleetLastViewed: Date
}

interface IVehicleSet {
    fleetVehicleSetID: number,
    vehicleSetName: string,
    vehicleTypeName: string,
    vehicleSecondaryTypeName: string,
    vehicleCount: number,
    fuelTypeName: string,
    usageValue: number,
    usageValueUnits: string,
    chargeHours: number
    imagePath: string
}

export const FleetSummary: React.FC<IFleetProps> = ({ fleetID, onVehicleCountLoaded }) => {
    const fetchWrapper = useFetchWrapper();
    const { addError, clearError } = useAPIErrorContext();

    const imageFilePath = import.meta.env.REACT_APP_IMAGE_LOCATION ? import.meta.env.REACT_APP_IMAGE_LOCATION + "/" : '';
    const applicationName = clientConfig?.APPLICATION_NAME || '';


    const navigate = useNavigate();
    const [fleetSummary, setFleetSummary] = useState<IFleetSummary>();



    useEffect(() => {
        getFleetByID(fleetID);
    }, [fleetID]);


    const getFleetByID = (summaryFleetID: any) => {
        fetchWrapper.get(`fleet/fleetSummary/${summaryFleetID}/1`)
            .then((data) => {
                if (data.fleetSummary) {
                    setFleetSummary(data.fleetSummary);
                    onVehicleCountLoaded?.(data.fleetSummary.totalVehicleCount);
                }
            });
    };

    const addVehicleSet = () => {
        navigate(`/AddVehicleSets/${fleetID}`);
    }

    const editVehicleSet = (vehicleSetID: any) => {
        navigate(`/EditVehicleSet/${fleetID}/${vehicleSetID}`)
    };

    const duplicateVehicleSet = (vehicleSetID: any, vehicleSetName: string) => {
        clearError();
        var duplicateVehicleSetRequest = {
            FleetVehicleSetID: vehicleSetID,
            FleetVehicleSetName: "Copy of " + vehicleSetName,
            ApplicationName: applicationName
        };

        fetchWrapper.post('vehicleSet/duplicate', 'application/json', duplicateVehicleSetRequest)
            .then((data) => {
                if (data.success) {
                    getFleetByID(fleetID);
                } else {
                    addError("Error duplicating vehicle set", data.message);
                }
            }).catch((error: any) => {
                addError("Error duplicating vehicle set", error);
            });
    };

    const deleteVehicleSet = async (vehicleSetID: any) => {
        if (window.confirm("Are you sure you want to delete this vehicle set?")) {
            clearError();
            var deleteVehicleSetRequest = {
                FleetVehicleSetID: vehicleSetID,
                FleetID: fleetID,
                ApplicationName: applicationName
            };

            fetchWrapper.post('vehicleSet/delete', 'application/json', deleteVehicleSetRequest)
                .then((data) => {
                    if (data.success) {
                        getFleetByID(fleetID);
                    } else {
                        addError("Error deleting vehicle set", data.message);
                    }
                }).catch((error: any) => {
                    addError("Error deleting vehicle set", error);
                });
        }
    };

    return (
        <div className="fleetSummaryData">
            <div className="fleetSummaryRow">
                <Row className="contentBlock">
                    <Col sm={12}>
                        <Row className="summary">
                            <Col lg={3} md={5} className="fleetCountColumn">
                                <div className="totalFleetCount">Fleet&nbsp;Summary:&nbsp;<strong>{fleetSummary?.totalVehicleCount}</strong>&nbsp;vehicles</div>
                            </Col>
                            {fleetSummary != null && fleetSummary.vehicleSets.length > 0 ? (
                                <Col lg={4} md={4}>
                                <Row>
                                    <Col className="leftBorder">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="71" height="15" viewBox="0 0 71 15" fill="none">
                                            <path d="M71 1H1V15" stroke="#0785F2" />
                                        </svg>
                                    </Col>
                                    <Col className="summaryHeading">
                                        By fuel type
                                    </Col>
                                    <Col className="rightBorder">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="71" height="15" viewBox="0 0 71 15" fill="none">
                                            <path d="M-2.08616e-06 1H70V15" stroke="#0785F2" />
                                        </svg>
                                    </Col>
                                </Row>
                                <Row>
                                        <Col className="summaryValues">
                                            {Object.keys(fleetSummary?.fuelTypes).map(key => (
                                                <span key={key} className="fleetSummaryItemList">{key}:&nbsp;<strong>{fleetSummary?.fuelTypes[key]}</strong></span>
                                            ))}
                                        </Col>
                                </Row>
                            </Col>
                            ) : (<Col></Col>)}
                        </Row>
                    </Col>
                </Row>
            </div>
            <div className="vehicleSetsContainer">
                <Row className="contentBlock contentCardBlock">
                            {fleetSummary?.vehicleSets.map((item: IVehicleSet, index: number) => {
                                return (
                                    <Col key={index} className="vehicleSetContent">
                                        <Card>
                                            <Row>
                                                <Col className="vSetTitle">
                                                    {item.vehicleSetName}
                                                </Col>
                                            </Row>
                                            <hr/>
                                            <Row>
                                                <Col lg={6} md={6} sm={6} xs={6} className="vSetDetails">
                                                    <div>{item.vehicleCount}&nbsp;{item.vehicleTypeName}</div>
                                                    <div>{item.fuelTypeName}</div>
                                                    <div>{item.usageValue}&nbsp;{item.usageValueUnits}</div>
                                                </Col>
                                                <Col lg={6} md={6} sm={6} xs={6}>
                                                    <div className="isoImageContainer">
                                                        <img src={`${imageFilePath}/${item.imagePath}`}/>
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row className="actionBarContainer">
                                                <Col>
                                                    <Row className="actionBar">
                                                        <Col>
                                                            <Button name="ViewEstimate" className="submitButton solidPos tertiaryButton iconRightArrowBlue editButton" onClick={() => { editVehicleSet(item.fleetVehicleSetID) }}>Edit</Button>
                                                            <Button name="ViewEstimate" className="submitButton noBgNeg tertiaryButton iconDoublePlusSign dupButton" onClick={() => { duplicateVehicleSet(item.fleetVehicleSetID, item.vehicleSetName) }}>Duplicate</Button>
                                                            <Button name="ViewEstimate" className="submitButton noBgNeg tertiaryButton iconTrash delButton" onClick={() => { deleteVehicleSet(item.fleetVehicleSetID) }}>Delete</Button>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                )
                            })}
                    <Col key="9999" className="vehicleSetContent">
                        <Card className="addVehicleSetCard" onClick={addVehicleSet}>
                            <div className="addVehicleSetDiv">
                                <svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M0.75 6.07837H6.07842L6.07842 0.749945L7.57842 0.749945L7.57842 7.57837L0.75 7.57837V6.07837Z" />
                                    <path fillRule="evenodd" clipRule="evenodd" d="M12.9062 7.57837L7.57783 7.57837L7.57783 12.9068L6.07782 12.9068L6.07783 6.07837L12.9063 6.07837L12.9062 7.57837Z" />
                                </svg>
                                {fleetSummary != null && fleetSummary.vehicleSets.length > 0 ? (
                                    <div>Add another Vehicle Set</div>
                                ) : (<div>Add a Vehicle Set</div>)}
                            </div>
                        </Card>
                    </Col>
                </Row>
                <Row className="contentBlock dateContent">
                    {fleetSummary != null && fleetSummary.vehicleSets.length > 0 ? (
                        <Col>
                            {fleetSummary?.fleetUpdatedDate ? (
                                <span>Last updated: {Helper.formatAsDateTime(fleetSummary?.fleetUpdatedDate)}. </span>
                            ) : ''}
                            {fleetSummary?.fleetCreatedDate ? (
                                <span>Created {Helper.formatAsDateTime(fleetSummary?.fleetCreatedDate)}.</span>
                            ) : ''}
                        </Col>
                    ) : (<Col></Col>)}
                </Row>
            </div>
        </div>
    );
};
